import { datadogRum } from '@datadog/browser-rum';

/**
 * Address confirmation modal for the onboarding flow
 *
 * Displays user-entered address vs Google's suggested address with a map preview.
 * Styled for dark mode to match the onboarding aesthetic.
 *
 * @see {@link https://developers.google.com/maps/documentation/javascript/styling} Google Maps Styling
 *
 * @example
 * ```tsx
 * <SuggestedAddressModal
 *   open={isOpen}
 *   userAddress={userEnteredAddress}
 *   suggestedAddress={googleSuggestedAddress}
 *   latitude="40.7128"
 *   longitude="-74.0060"
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={(address, option) => handleConfirm(address, option)}
 * />
 * ```
 */
import { Button, ModalOverlay } from '@trm/ui-kit';
import { cx } from '@trm/ui-kit/utils';
import { useEffect, useState } from 'react';
import {
  Modal as AriaModal,
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
} from 'react-aria-components';
import { AddressProps } from '~/hooks/useGoogleAddressValidation';
import '~/pages/auth/auth-shared.css';
import axiosServices from '~/utils/axios';

type SuggestedAddressModalProps = {
  userAddress: AddressProps | null;
  suggestedAddress: AddressProps | null;
  latitude?: string;
  longitude?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (address: AddressProps | null, selectedOption: 'user' | 'suggested') => void;
};

export const SuggestedAddressModal: React.FC<SuggestedAddressModalProps> = ({
  userAddress,
  suggestedAddress,
  latitude,
  longitude,
  open,
  onClose,
  onConfirm,
}) => {
  const [mapUrl, setMapUrl] = useState('');
  const [selectedOption, setSelectedOption] = useState<'user' | 'suggested'>('suggested');

  useEffect(() => {
    if (!latitude || !longitude) return;
    const controller = new AbortController();
    axiosServices
      .get<{ url: string }>('/me/map-url', {
        params: { lat: latitude, lng: longitude },
        signal: controller.signal,
      })
      .then(({ data }) => setMapUrl(data.url))
      .catch((error) => {
        if (error.name === 'CanceledError') {
          return;
        }
        datadogRum.addError(error, {
          errorType: 'SuggestedAddressModalError',
          message: 'Failed to load map URL',
          operation: 'fetchMapUrl',
          context: { latitude, longitude },
        });
      });

    return () => {
      controller.abort();
    };
  }, [latitude, longitude]);

  return (
    <ModalOverlay isOpen={open} onOpenChange={(isOpen) => !isOpen && onClose()} isDismissable>
      <AriaModal
        className={cx(
          'max-h-full w-full max-w-100 rounded-xl bg-[var(--auth-bg-primary-subtle)] text-white shadow-2xl outline-hidden',
          'duration-300 animate-in fade-in zoom-in-95',
        )}
      >
        {/* Header */}
        <div className="px-6 pt-6">
          <h2 className="text-md font-semibold text-white">Confirm your address</h2>
          <p className="mt-1 text-sm text-tertiary">
            Review the recommended changes and confirm your address.
          </p>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-6">
          <AriaRadioGroup
            value={selectedOption}
            onChange={(value) => setSelectedOption(value as 'user' | 'suggested')}
            className="flex flex-col gap-2"
          >
            {/* User Entered Address */}
            <AriaRadio
              value="user"
              className={({ isSelected, isFocusVisible }) =>
                cx(
                  'flex cursor-pointer items-start gap-4 rounded-lg border p-4 outline-hidden transition-colors',
                  isSelected
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-gray-700 bg-[var(--auth-bg-primary-subtle)]',
                  isFocusVisible && 'ring-2 ring-blue-500 ring-offset-2 ring-offset-black',
                )
              }
            >
              {({ isSelected }) => (
                <>
                  {/* Radio Circle */}
                  <div
                    className={cx(
                      'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                      isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300',
                    )}
                  >
                    {isSelected && <div className="size-2 rounded-full bg-white" />}
                  </div>

                  {/* Address Content */}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">What you entered</div>
                    <div className="mt-1 text-sm text-gray-300">
                      {userAddress?.addressLines
                        ?.filter((line) => line && line.trim() !== '')
                        .join(', ')}
                    </div>
                    <div className="text-sm text-gray-300">
                      {userAddress?.locality}, {userAddress?.administrativeArea}{' '}
                      {userAddress?.postalCode}
                    </div>
                  </div>
                </>
              )}
            </AriaRadio>

            {/* Suggested Address with Map */}
            <AriaRadio
              value="suggested"
              className={({ isSelected, isFocusVisible }) =>
                cx(
                  'flex cursor-pointer flex-col rounded-lg border p-4 outline-hidden transition-colors',
                  isSelected
                    ? 'border-blue-500'
                    : 'border-gray-700 bg-[var(--auth-bg-primary-subtle)]',
                  isFocusVisible && 'ring-2 ring-blue-500 ring-offset-2 ring-offset-black',
                )
              }
            >
              {({ isSelected }) => (
                <>
                  <div className="flex items-start gap-4">
                    {/* Radio Circle */}
                    <div
                      className={cx(
                        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                        isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-500',
                      )}
                    >
                      {isSelected && <div className="size-1.5 rounded-full bg-white" />}
                    </div>

                    {/* Address Content */}
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white">Recommended</div>
                      <div className="mt-1 text-sm text-secondary">
                        {suggestedAddress?.addressLines
                          ?.filter((line) => line && line.trim() !== '')
                          .join(', ')}
                      </div>
                      <div className="text-sm text-secondary">
                        {suggestedAddress?.locality}, {suggestedAddress?.administrativeArea}{' '}
                        {suggestedAddress?.postalCode}
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  {mapUrl && (
                    <div className="mt-3 overflow-hidden rounded-lg bg-[var(--auth-bg-primary-subtle)]">
                      <iframe
                        title="Suggested Address Map"
                        height="200"
                        width="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={mapUrl}
                      />
                    </div>
                  )}
                </>
              )}
            </AriaRadio>
          </AriaRadioGroup>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 px-6 pb-4 sm:flex-row">
          <Button
            onClick={onClose}
            className="w-full border border-white bg-transparent text-md text-white hover:bg-white/10 sm:w-auto sm:flex-1"
          >
            Back
          </Button>
          <Button
            onClick={() => {
              const selected = selectedOption === 'suggested' ? suggestedAddress : userAddress;
              onConfirm(selected, selectedOption);
            }}
            className="w-full bg-white text-md text-black hover:bg-gray-200 sm:w-auto sm:flex-1"
          >
            Confirm address
          </Button>
        </div>
      </AriaModal>
    </ModalOverlay>
  );
};
